import { useEffect, useRef, useState, type FormEvent } from 'react';
import type {
  AdminChannel,
  AdminChannelDetail,
  AdminUser,
  ChannelType,
} from '@orbit-sync/shared-types';
import { Avatar } from '../../components/Avatar.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { EmptyState } from '../../components/EmptyState.js';
import { FormBanner } from '../../components/FormBanner.js';
import { Icon } from '../../components/Icon.js';
import { api, errorMessage } from '../../lib/api/index.js';
import { useSettingsStore } from '../../settings/settingsStore.js';
import { formatDate, useDebounced } from '../adminHelpers.js';
import { PAGE_SIZE, Pager, SearchBox } from '../adminUi.js';
import { useAdminQuery } from '../useAdminQuery.js';

type Pending =
  | { kind: 'delete'; channel: AdminChannel }
  | { kind: 'clear'; channel: AdminChannel }
  | { kind: 'clear-all' };

const TYPES: { value: ChannelType | undefined; label: string }[] = [
  { value: undefined, label: 'All' },
  { value: 'direct', label: 'Direct' },
  { value: 'group', label: 'Groups' },
];

/** Every channel: view, rename and re-member groups, clear history, delete. */
export function AdminChannelsPage() {
  const [query, setQuery] = useState('');
  const [type, setType] = useState<ChannelType | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const q = useDebounced(query.trim());
  const { data, error, loading, reload } = useAdminQuery(
    (signal) => api.admin.listChannels({ q, type, limit: PAGE_SIZE, offset }, signal),
    `${q}|${type ?? ''}|${offset}`,
  );

  async function confirm(): Promise<void> {
    if (!pending) {
      return;
    }

    setBusy(true);
    setActionError(null);

    try {
      if (pending.kind === 'delete') {
        await api.admin.deleteChannel(pending.channel.id);
        setNotice(`Deleted “${pending.channel.name}”.`);
      } else if (pending.kind === 'clear') {
        const { deletedMessages } = await api.admin.clearChannelHistory(pending.channel.id);
        setNotice(
          `Cleared ${deletedMessages} message${deletedMessages === 1 ? '' : 's'} from “${pending.channel.name}”.`,
        );
      } else {
        const { deletedMessages } = await api.admin.clearAllHistory();
        setNotice(
          `Cleared ${deletedMessages} message${deletedMessages === 1 ? '' : 's'} from every channel.`,
        );
      }

      setPending(null);
      reload();
    } catch (cause) {
      setActionError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="admin-page">
      <header className="admin-head">
        <div>
          <h1>Channels</h1>
          <p className="muted small">Every direct conversation and group in the workspace.</p>
        </div>
        <button
          type="button"
          className="btn btn--danger"
          onClick={() => {
            setActionError(null);
            setPending({ kind: 'clear-all' });
          }}
        >
          <Icon name="eraser" size={14} />
          Clear all history
        </button>
      </header>

      <div className="admin-toolbar">
        <SearchBox
          value={query}
          placeholder="Search channels"
          onChange={(value) => {
            setQuery(value);
            setOffset(0);
          }}
        />
        <div className="mode-switch admin-filter" role="tablist" aria-label="Channel type">
          {TYPES.map((option) => (
            <button
              key={option.label}
              type="button"
              role="tab"
              aria-selected={type === option.value}
              className={type === option.value ? 'mode-tab mode-tab--active' : 'mode-tab'}
              onClick={() => {
                setType(option.value);
                setOffset(0);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
        {loading ? <span className="btn-spinner muted" aria-label="Loading" /> : null}
      </div>

      <FormBanner message={error} />
      {notice ? (
        <p className="admin-notice" role="status">
          <Icon name="check" size={14} />
          {notice}
        </p>
      ) : null}

      {data && data.items.length === 0 ? (
        <EmptyState compact icon="messagesEmpty" title="No channels match" />
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th className="num">Members</th>
                <th className="num">Messages</th>
                <th>Last activity</th>
                <th>Created</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((channel) => (
                <tr key={channel.id}>
                  <td>
                    <span className="cell-person">
                      <Avatar
                        name={channel.name}
                        id={channel.id}
                        size="sm"
                        group={channel.type === 'group'}
                      />
                      <span className="truncate" dir="auto">
                        {channel.name}
                      </span>
                    </span>
                  </td>
                  <td>
                    <span className={`tag tag--${channel.type}`}>
                      {channel.type === 'direct' ? 'Direct' : 'Group'}
                    </span>
                  </td>
                  <td className="num">{channel.memberIds.length}</td>
                  <td className="num">{channel.messageCount}</td>
                  <td>{formatDate(channel.lastMessageAt, true)}</td>
                  <td>{formatDate(channel.createdAt)}</td>
                  <td className="cell-actions">
                    <button
                      type="button"
                      className="btn btn--icon btn--sm"
                      aria-label={`View ${channel.name}`}
                      title={channel.type === 'group' ? 'View and edit' : 'View'}
                      onClick={() => setEditingId(channel.id)}
                    >
                      <Icon name={channel.type === 'group' ? 'pencil' : 'users'} size={14} />
                    </button>
                    <button
                      type="button"
                      className="btn btn--icon btn--sm"
                      aria-label={`Clear history of ${channel.name}`}
                      title="Clear history"
                      disabled={channel.messageCount === 0}
                      onClick={() => {
                        setActionError(null);
                        setPending({ kind: 'clear', channel });
                      }}
                    >
                      <Icon name="eraser" size={14} />
                    </button>
                    <button
                      type="button"
                      className="btn btn--icon btn--sm btn--icon-danger"
                      aria-label={`Delete ${channel.name}`}
                      title="Delete"
                      onClick={() => {
                        setActionError(null);
                        setPending({ kind: 'delete', channel });
                      }}
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data ? <Pager offset={offset} total={data.total} onChange={setOffset} /> : null}

      {editingId ? (
        <ChannelDialog
          channelId={editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            setEditingId(null);
            reload();
          }}
        />
      ) : null}

      {pending ? (
        <ConfirmDialog
          title={
            pending.kind === 'clear-all'
              ? 'Clear ALL chat history?'
              : pending.kind === 'clear'
                ? `Clear history of “${pending.channel.name}”?`
                : `Delete “${pending.channel.name}”?`
          }
          confirmLabel={pending.kind === 'delete' ? 'Delete channel' : 'Clear history'}
          requireText={pending.kind === 'clear-all' ? 'CLEAR ALL' : undefined}
          busy={busy}
          error={actionError}
          onConfirm={() => void confirm()}
          onCancel={() => setPending(null)}
        >
          <p>
            {pending.kind === 'clear-all'
              ? 'Every message in every channel is deleted for everyone. Channels and their members stay. This cannot be undone.'
              : pending.kind === 'clear'
                ? `All ${pending.channel.messageCount} messages are deleted for everyone in it. The channel and its members stay.`
                : 'The channel and its whole history are deleted, and it disappears from every member’s sidebar.'}
          </p>
        </ConfirmDialog>
      ) : null}
    </section>
  );
}

function ChannelDialog({
  channelId,
  onClose,
  onSaved,
}: {
  channelId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const maxName = useSettingsStore((state) => state.settings.maxChannelNameLength);
  const ref = useRef<HTMLDialogElement>(null);
  const { data: detail, error: loadError } = useAdminQuery(
    (signal) => api.admin.getChannel(channelId, signal),
    channelId,
  );

  useEffect(() => {
    ref.current?.showModal();
  }, []);

  return (
    <dialog
      ref={ref}
      className="modal modal--wide"
      onClose={onClose}
      aria-labelledby="channel-title"
    >
      {detail ? (
        <ChannelForm detail={detail} maxName={maxName} onClose={onClose} onSaved={onSaved} />
      ) : (
        <>
          <header className="modal-head">
            <h2 id="channel-title">Channel</h2>
            <button type="button" className="btn btn--icon" onClick={onClose} aria-label="Close">
              <Icon name="x" size={17} />
            </button>
          </header>
          <div className="modal-scroll">
            {loadError ? <FormBanner message={loadError} /> : <span className="btn-spinner" />}
          </div>
        </>
      )}
    </dialog>
  );
}

/** Mounted once the detail has loaded, so its state starts from the real values. */
function ChannelForm({
  detail,
  maxName,
  onClose,
  onSaved,
}: {
  detail: AdminChannelDetail;
  maxName: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isGroup = detail.type === 'group';
  const [name, setName] = useState(detail.name);
  const [members, setMembers] = useState<{ id: string; displayName: string; email: string }[]>(
    detail.members,
  );
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const q = useDebounced(search.trim());
  const { data: found } = useAdminQuery(
    (signal) =>
      q.length >= 2
        ? api.admin.listUsers({ q, limit: 8, offset: 0 }, signal)
        : Promise.resolve({ items: [] as AdminUser[], total: 0, limit: 8, offset: 0 }),
    `members:${q}`,
  );
  const candidates = (found?.items ?? []).filter(
    (user) => !members.some((member) => member.id === user.id),
  );

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    const changes: { name?: string; memberIds?: string[] } = {};

    if (name.trim() !== detail.name) {
      changes.name = name.trim();
    }

    const ids = members.map((member) => member.id);
    if (
      ids.length !== detail.memberIds.length ||
      ids.some((id) => !detail.memberIds.includes(id))
    ) {
      changes.memberIds = ids;
    }

    if (!changes.name && !changes.memberIds) {
      onClose();
      return;
    }

    if (members.length < 2) {
      setError('A group needs at least two members.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await api.admin.updateChannel(detail.id, changes);
      onSaved();
    } catch (cause) {
      setError(errorMessage(cause));
      setBusy(false);
    }
  }

  return (
    <form className="modal-form" onSubmit={(event) => void submit(event)} noValidate>
      <header className="modal-head">
        <div>
          <h2 id="channel-title" dir="auto">
            {detail.name}
          </h2>
          <p>
            {isGroup ? 'Group' : 'Direct conversation'} · {detail.messageCount} messages · created{' '}
            {formatDate(detail.createdAt)}
          </p>
        </div>
        <button type="button" className="btn btn--icon" onClick={onClose} aria-label="Close">
          <Icon name="x" size={17} />
        </button>
      </header>

      <div className="modal-scroll">
        <FormBanner message={error} />

        {isGroup ? (
          <div className="field">
            <label htmlFor="channel-name">Group name</label>
            <input
              id="channel-name"
              dir="auto"
              maxLength={maxName}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
        ) : (
          <p className="muted small">
            A direct conversation belongs to its two participants, so it cannot be renamed or have
            its members changed.
          </p>
        )}

        <span className="field-label">Members · {members.length}</span>
        <ul className="member-list">
          {members.map((member) => (
            <li key={member.id}>
              <Avatar name={member.displayName} id={member.id} size="sm" />
              <span className="member-body">
                <span className="truncate" dir="auto">
                  {member.displayName}
                </span>
                <span className="muted small truncate">{member.email}</span>
              </span>
              {isGroup ? (
                <button
                  type="button"
                  className="btn btn--icon btn--sm btn--icon-danger"
                  aria-label={`Remove ${member.displayName}`}
                  onClick={() =>
                    setMembers((current) => current.filter((item) => item.id !== member.id))
                  }
                >
                  <Icon name="x" size={14} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>

        {isGroup ? (
          <div className="field">
            <label htmlFor="member-search">Add people</label>
            <SearchBox value={search} onChange={setSearch} placeholder="Name or email" />
            {candidates.length > 0 ? (
              <ul className="result-list">
                {candidates.map((user) => (
                  <li key={user.id}>
                    <button
                      type="button"
                      className="result"
                      onClick={() => {
                        setMembers((current) => [...current, user]);
                        setSearch('');
                      }}
                    >
                      <Avatar name={user.displayName} id={user.id} size="sm" />
                      <span className="result-body">
                        <span className="result-name" dir="auto">
                          {user.displayName}
                        </span>
                      </span>
                      <span className="result-action">
                        <Icon name="plus" size={13} />
                        Add
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>

      <footer className="modal-foot">
        <button type="button" className="btn btn--ghost" onClick={onClose}>
          {isGroup ? 'Cancel' : 'Close'}
        </button>
        {isGroup ? (
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? <span className="btn-spinner" /> : <Icon name="check" size={14} />}
            Save changes
          </button>
        ) : null}
      </footer>
    </form>
  );
}
